import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type MutableRefObject,
  type ReactNode,
} from "react";
/** 0 idle, 1 "Sending…" (pending > 300 ms), 2 "Still sending…" (pending > 3 s). */
export type SendingStage = 0 | 1 | 2;
type ActionStatus = {
  stage: SendingStage;
  /** The submit that was pressed last, so only it reports progress. */
  last: MutableRefObject<string | null>;
};
const fallback: ActionStatus = { stage: 0, last: { current: null } };
export const ActionStatusContext = createContext<ActionStatus>(fallback);
export function useSendingStage(pending: boolean | undefined): SendingStage {
  const [stage, setStage] = useState<SendingStage>(0);
  useEffect(() => {
    if (!pending) {
      setStage(0);
      return;
    }
    const slow = setTimeout(() => setStage(1), 300),
      stalled = setTimeout(() => setStage(2), 3000);
    return () => {
      clearTimeout(slow);
      clearTimeout(stalled);
    };
  }, [pending]);
  return pending ? stage : 0;
}
export function useActionStatus(stage: SendingStage): ActionStatus {
  const last = useRef<string | null>(null);
  return { stage, last };
}
/** The one primary submit of an action. While this seat's action awaits the
 * server for more than 300 ms it reads "Sending…" with a small spinner, then
 * "Still sending…" after 3 s. Driven by `pending`, never by `disabled`. */
export function SubmitButton({
  children,
  className = "primary full",
  onClick,
  type = "submit",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  const status = useContext(ActionStatusContext);
  const id = useId();
  const mine = status.last.current === null || status.last.current === id;
  const sending = status.stage > 0 && mine;
  return (
    <button
      data-tour="primary"
      {...rest}
      type={type}
      className={`${className}${sending ? " is-sending" : ""}`}
      aria-busy={sending || undefined}
      onClick={(e) => {
        status.last.current = id;
        onClick?.(e);
      }}
    >
      {sending ? (
        <>
          <span className="play-spinner" aria-hidden="true" />
          {status.stage === 2 ? "Still sending…" : "Sending…"}
        </>
      ) : (
        children
      )}
    </button>
  );
}
